import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import backarrow from "../../../../asset/images/arrow-backicon.svg";
import sendBackIcon from "../../../../asset/images/sentback.svg";
import Header from "../../../../Components/Header";
import SingleSignOnSideBar from "../../../../Components/SingleSignOnSideBar";
import Loader from "../../../../Components/Common/Loader";
import { Dealer_Mapping } from "../../../../Configuration/API_URL";
import { decryptData, noDataFound } from "../../../../CommonMethhods/CommonMethods";
import axiosInstance from "../../../../CommonMethhods/axiosInstance";
import NewMappingReq from "../NewMappingReq";
import BulkUpload from "../BulkUpload";
import SingleRequestDetails from "./SingleRequestDetails";
import BulkRequestDetails from "./BulkRequestDetails";

// Draft / Send back requests stay editable for the maker, every other
// status (pending / approved / rejected) is read-only.
// A sent back batch comes as "sendback", a sent back row as "sent_back".
// const SENT_BACK_STATUSES = ["sendback", "send back", "sent_back"];
// const EDITABLE_STATUSES = ["draft", ...SENT_BACK_STATUSES];
// The backend spells it several ways (sendback, send back, send_back,
// sent_back ...), so statuses are compared with spaces, "_" and "-" removed.
const normalizeStatus = (value = "") =>
  String(value).toLowerCase().replace(/[\s_-]/g, "");
const SENT_BACK_STATUSES = ["sendback", "sentback"];
const EDITABLE_STATUSES = ["draft", ...SENT_BACK_STATUSES];

function DealerRequestDetails() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Row handed over by the dashboard - kept only as an instant fallback so the
  // page still renders correctly on refresh / direct URL, where it is absent.
  const [summary, setSummary] = useState(location.state?.request || null);
  const [rowData, setRowData] = useState(null);
  const [loader, setLoader] = useState(true);

  const isBulk = String(batchId || "").toUpperCase().startsWith("BLK");

  const status = (
    summary?.status ||
    rowData?.status ||
    rowData?.requests?.[0]?.approvalStatus ||
    ""
  ).toString();

  // const isEditable = EDITABLE_STATUSES.includes(status.trim().toLowerCase());
  // const isSentBack = SENT_BACK_STATUSES.includes(status.trim().toLowerCase());
  const isEditable = EDITABLE_STATUSES.includes(normalizeStatus(status));
  const isSentBack = SENT_BACK_STATUSES.includes(normalizeStatus(status));

  // The checker's send back reason, so the maker knows what to change before
  // resubmitting.
  const sendBackRemarks = [
    ...new Set(
      (rowData?.requests || [])
        .flatMap((row) =>
          Array.isArray(row?.sendBackReason)
            ? row.sendBackReason
            : [row?.sendBackReason]
        )
        .filter(Boolean)
    ),
  ].join(", ");

  const goToDashboard = (tab) => {
    navigate("/dealer-mapping", { state: tab ? { activeTab: tab } : undefined });
  };

  const readError = (error) => {
    try {
      return JSON.parse(decryptData(error?.response?.data))?.message;
    } catch {
      return "";
    }
  };

  // Batch details - the same endpoint the dashboard used for the inline preview.
  const getRequestDetails = async () => {
    try {
      const response = await axiosInstance.get(
        `${Dealer_Mapping.validationRowPreview}/${batchId}`
      );
      const decrypted = JSON.parse(decryptData(response?.data));

      if (decrypted?.status === 0) {
        setRowData(decrypted?.data);
      } else {
        toast.error(decrypted?.message);
      }
    } catch (error) {
      const message = readError(error);
      if (message) toast.error(message);
      console.log(error);
    }
  };

  // On refresh / direct URL there is no row from the dashboard, so the request
  // summary (status, count, reason) is pulled from the history grid by SR.
  const getRequestSummary = async () => {
    try {
      const queryParams = new URLSearchParams({
        format: "grid",
        status: "all",
        type: "all",
        fromDate: "",
        toDate: "",
        batchIdRef: batchId,
      });

      const response = await axiosInstance.get(
        `${Dealer_Mapping.validationRowPreview}?${queryParams}`
      );
      const decrypted = JSON.parse(decryptData(response?.data));

      if (decrypted?.status === 0) {
        const match = (decrypted?.data || []).find(
          (item) =>
            item?.batchId?.toUpperCase() === String(batchId).toUpperCase()
        );
        if (match) setSummary(match);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!batchId) {
      setLoader(false);
      return;
    }

    let isActive = true;

    const loadDetails = async () => {
      setLoader(true);
      await Promise.all([
        getRequestDetails(),
        location.state?.request ? Promise.resolve() : getRequestSummary(),
      ]);
      if (isActive) setLoader(false);
    };

    loadDetails();

    return () => {
      isActive = false;
    };
  }, [batchId]);

  const renderContent = () => {
    if (loader) {
      return (
        <div className="loader-alignment">
          <Loader />
        </div>
      );
    }

    if (!batchId || !rowData) {
      return (
        <div className="step-content report-error-message">
          {noDataFound("Request details are not available")}
        </div>
      );
    }

    if (isEditable) {
      // Reuse the maker screens - they already own the validation, the
      // submit / save-as-draft APIs and the loaders for both request types.
      const selectedRequest = summary || { batchId, status };

      return (
        <div className="wrap-all-tabs">
          {isSentBack && sendBackRemarks && (
            <div className="reject-remark-cont">
              <div>
                <img src={sendBackIcon} alt="sent back" />
              </div>
              <div className="wrap-content">
                <span className="bold-text">Checker Send Back Remarks</span>
                <span className="remarks-val">{sendBackRemarks}</span>
              </div>
            </div>
          )}
          {isBulk ? (
            <BulkUpload
              selectedRequest={selectedRequest}
              isFromMyRequest={true}
              setSelectedSR={() => goToDashboard()}
              refreshHistory={() => goToDashboard()}
              rowData={rowData}
              handleTabChange={(tab) => goToDashboard(tab)}
            />
          ) : (
            <NewMappingReq
              selectedRequest={selectedRequest}
              isFromMyRequest={true}
              setSelectedSR={() => goToDashboard()}
              refreshHistory={() => goToDashboard()}
              rowData={rowData}
              isReadOnly={false}
            />
          )}
        </div>
      );
    }

    return isBulk ? (
      <BulkRequestDetails
        batchId={batchId}
        status={status}
        rowData={rowData}
        summary={summary}
      />
    ) : (
      <SingleRequestDetails
        batchId={batchId}
        status={status}
        rowData={rowData}
        summary={summary}
      />
    );
  };

  return (
    <div className="dealer-mapping-main">
      <Header portal={""} showUserId />
      <SingleSignOnSideBar />
      <main id="dealermap-main-content">
        <div className="tab-content card-box cl-mapping-tab">
          <div className="ckr-page new-upload">
            <button
              type="button"
              className="dlr-go-back"
              onClick={() => goToDashboard()}
            >
              <img src={backarrow} alt="back" />
              <span className="back-title">Go Back</span>
            </button>

            {renderContent()}
          </div>
        </div>
      </main>
    </div>
  );
}

export default DealerRequestDetails;
